import { useMemo } from "react";
import type { Database } from "@/lib/supabase/supabase-types";

type ClientProfile = Database["public"]["Tables"]["client_profiles"]["Row"];

export interface ClientCompletionItem {
  id: string;
  label: string;
  completed: boolean;
  description?: string;
  route?: string;
  section?: string;
}

export interface ClientProfileCompletionResult {
  items: ClientCompletionItem[];
  percentage: number;
  completedCount: number;
  totalCount: number;
  isComplete: boolean;
  nextIncompleteItem: ClientCompletionItem | null;
}

/**
 * Hook to calculate client profile completion status
 * @param profile - The client profile data
 */
export function useClientProfileCompletion(
  profile: ClientProfile | null | undefined
): ClientProfileCompletionResult {
  return useMemo(() => {
    if (!profile) {
      return {
        items: [],
        percentage: 0,
        completedCount: 0,
        totalCount: 3,
        isComplete: false,
        nextIncompleteItem: null,
      };
    }

    // Define completion criteria for client profiles
    // Note: Website and logo are optional - not required for onboarding
    // Note: Email was added later via migration, so existing profiles may have null email
    const items: ClientCompletionItem[] = [
      {
        id: "basic",
        label: "Basic Information",
        description: "Company name and contact email",
        // company_name is NOT NULL, but email is nullable (added in migration 0024)
        completed: !!(profile.company_name && profile.email),
        route: "/client/profile",
        section: "basic",
      },
      {
        id: "about",
        label: "About Your Business",
        description: "Company description",
        // Bio is nullable, and must be at least 50 characters if present
        completed: !!(profile.bio && profile.bio.length >= 50),
        route: "/client/profile",
        section: "about",
      },
      {
        id: "details",
        label: "Company Details",
        description: "Industry, size, and location",
        // All three fields are nullable, so check all are present
        completed: !!(
          profile.industry &&
          profile.company_size &&
          profile.location
        ),
        route: "/client/profile",
        section: "details",
      },
    ];

    const completedCount = items.filter((i) => i.completed).length;
    const percentage = Math.round((completedCount / items.length) * 100);
    const nextIncompleteItem = items.find((i) => !i.completed) || null;

    return {
      items,
      percentage,
      completedCount,
      totalCount: items.length,
      isComplete: completedCount === items.length,
      nextIncompleteItem,
    };
  }, [profile]);
}
